// ============================================================================
// Porter tracking-link checking.
//
// A Porter booking is made by hand on the Porter app, so OMS never hears from
// the carrier. The backend's status sweep instead ASKS Porter whether the trip
// finished, and marks the leg delivered when it has — keyed on the `booking_id`
// + `customer_uuid` carried in the live-tracking link.
//
// So the link is what makes a booking close itself. It is NOT compulsory: a
// Porter booking saves fine without one, it simply stays a manual close, the
// way every Porter booking is today. Nothing here ever blocks a save.
//
// What it does do is flag the one case that fails SILENTLY: a link that looks
// right but carries no tracking details (the plain porter.in page rather than
// the trip's share link). Blank is an informed choice; a dead link is someone
// believing they set this up. That is a warning — visible, never blocking.
//
// Mirrors `parse_tracking_link` in the backend's
// oms/logistics/integrations/porter_tracking.py — keep the two in step.
// ============================================================================
import type { Rule } from "antd/es/form";

export interface PorterLinkParts {
  orderId: string;
  customerUuid: string;
}

/**
 * Pull the booking params out of a Porter live-tracking link, or null if they
 * aren't both there. Param order doesn't matter, and `order_id` is accepted as
 * an alias for `booking_id` (Porter uses one in the share link, the other in
 * the API call).
 */
export const parsePorterLink = (url?: string | null): PorterLinkParts | null => {
  if (!url || typeof url !== "string") return null;
  let params: URLSearchParams;
  try {
    params = new URL(url.trim()).searchParams;
  } catch {
    return null; // not a URL at all
  }
  const orderId = (params.get("booking_id") || params.get("order_id") || "").trim();
  const customerUuid = (params.get("customer_uuid") || "").trim();
  if (!orderId || !customerUuid) return null;
  return { orderId, customerUuid };
};

export const isTrackablePorterLink = (url?: string | null): boolean =>
  parsePorterLink(url) !== null;

const UNTRACKABLE =
  "This link has no tracking details, so this delivery won't close itself — " +
  "use the share link from the Porter app (it contains booking_id and " +
  "customer_uuid) if you want that. You can still save.";

/**
 * Form rules for a tracking-URL field. `isPorter` adds the trackable-link
 * warning; the URL-format rule is what every provider already had.
 *
 * Never required, and the Porter check is `warningOnly` — antd shows the
 * message and still lets the form submit.
 */
export const trackingUrlRules = (isPorter: boolean): Rule[] => {
  const rules: Rule[] = [{ type: "url", message: "Enter a valid URL" }];
  if (!isPorter) return rules;
  rules.push({
    warningOnly: true,
    validator: (_, value) =>
      !value?.trim() || isTrackablePorterLink(value)
        ? Promise.resolve()
        : Promise.reject(new Error(UNTRACKABLE)),
  });
  return rules;
};
